import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { FloorActionsTab } from "@/pages/FloorActionsTab";
import { useAuth } from "@/context/AuthContext";
import { Smartphone, Clock } from "lucide-react";

// HIS SETTING, 2026-10-08: *"5 minutes. Make it a setting I can change later if my crew finds it
// too short or too long."* Nothing is ever lost when a location clears — every part is recorded
// the moment it beeps — it exists so the NEXT person cannot inherit a location somebody else
// loaded and walked away from.
function PutawayIdleSetting({ canEdit }) {
  const [mins, setMins] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = () => api.get("/putaway/settings")
    .then(({ data }) => setMins(data.idle_clear_minutes)).catch(() => {});
  useEffect(() => { reload(); }, []);

  const save = async (val) => {
    const m = parseInt(val, 10);
    if (!Number.isInteger(m) || m < 1 || m > 60) {
      toast.error("Enter a whole number of minutes between 1 and 60."); reload(); return;
    }
    if (m === mins) return;
    setSaving(true);
    try {
      const { data } = await api.put("/putaway/settings", { idle_clear_minutes: m });
      setMins(data.idle_clear_minutes);
      toast.success(`A loaded location now clears after ${data.idle_clear_minutes} minutes of no scanning.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save that.");
      reload();
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="putaway-idle-setting" className="border border-slate-200 bg-white p-4 mb-5">
      <div className="flex items-center gap-2 font-display text-lg font-bold tracking-tight">
        <Clock className="w-5 h-5 text-[#E0781A]" />Put-away — clear a loaded location
      </div>
      <p className="text-sm text-slate-500 mt-1 mb-3">
        On the floor screen a scanned location stays loaded while parts go into it. If nobody
        scans for this long it clears itself, so the next person can't put parts into somebody
        else's bin. The banner counts down for the last 30 seconds. Nothing is lost either way —
        every part is recorded the moment it beeps.
      </p>
      <div className="flex items-center gap-2">
        <Input data-testid="putaway-idle-minutes" type="number" min={1} max={60}
          className="w-24 font-mono h-10" disabled={!canEdit || saving || mins === null}
          value={mins ?? ""} onChange={(e) => setMins(e.target.value === "" ? "" : parseInt(e.target.value, 10))}
          onBlur={(e) => save(e.target.value)} />
        <span className="text-sm font-semibold text-slate-600">minutes of no scanning</span>
      </div>
    </div>
  );
}

// FLOOR BUTTONS — configuration for the TABLET surface, and that is why it lives here beside
// Printer Setup rather than under Vehicle Setup, where it sat next to Categories, Stages and Spec
// Fields. Those are lists about vehicles; this is about a screen your crew holds in their hands.
// The owner found the mismatch by accident, which is the argument for moving it.
export default function FloorButtonsPage() {
  const { has } = useAuth();
  return (
    <div data-testid="floor-buttons-page">
      <div className="mb-5">
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
          <Smartphone className="w-6 h-6 text-[#E0781A]" />Floor Buttons
        </h1>
        <p className="text-sm text-slate-500">
          Rename, reorder and show/hide the buttons your crew taps on the Floor (tablet) surface.
          Every button maps to an action that already exists — you can't create one with nothing
          behind it.
        </p>
      </div>
      <PutawayIdleSetting canEdit={has("manage_settings")} />
      <FloorActionsTab canEdit={has("manage_lists")} />
    </div>
  );
}
